import { BehaviorAnalyzer } from "@/core/battle/ai/BehaviorAnalyzer";
import { DistanceMatrixCalculator } from "@/core/battle/ai/calculators/DistanceMatrixCalculator";
// import { OpportunityRiskCalculator } from "@/core/battle/ai/calculators/OpportunityRiskCalculator";
import { PathsToEnemyCalculator } from "@/core/battle/ai/calculators/PathsToEnemyCalculator";
import { ProtectionMapCalculator } from "@/core/battle/ai/calculators/ProtectionMapCalculator";
import { ThreatMapCalculator } from "@/core/battle/ai/calculators/ThreatMapCalculator";
import { ZocCalculator } from "@/core/battle/ai/calculators/ZocCalculator";

import { Goal, GoalType } from "@/core/battle/ai/strategic/AIGoals";
import { StrategicStance, PlanningContext, PrecomputedHexData, HexDistanceInfo } from "@/core/battle/ai/types/ai.types";
import { getStanceWeights } from "@/core/battle/ai/utils/AIConfig";
import { EvaluatorUtils } from "@/core/battle/ai/utils/evaluatorUtils";
import { CombatCalculator } from "@/core/battle/CombatCalculator";
import { PathCalculator } from "@/core/battle/PathCalculator";
import { BattleUnit, Hex } from "@/core/types/battle.types";
import { getUnitTotalHp } from "@/core/utils/battle.utils";
import { idToCoords } from "@/core/utils/hex.utils";
import { useBattleStore } from "@/store/useBattleStore";

export class AIContext {

    public static create(
        unit: BattleUnit,
        units: Map<string, BattleUnit>,
        battleMap: Map<string, Hex>,
        stance: StrategicStance,
        goal: Goal | null, 
        round: number,
    ): PlanningContext {
        
        // 1. Визначаємо ваги на основі стійки (Stance)
        const weights = getStanceWeights(stance);
        
        const behavior = BehaviorAnalyzer.analyzeBehavior(unit);

        const enemies: BattleUnit[] = [];
        const allies: BattleUnit[] = [];
        let totalEnemiesValue: number = 0;
        let totalAlliesValue: number = 0;
        for (const u of units.values()) {
            if (u.isDead) continue;
            const stackValue = EvaluatorUtils.calculateStackPower(u);
            if (u.faction === unit.faction) {
                allies.push(u);
                totalAlliesValue += stackValue;
            } else {
                enemies.push(u);
                totalEnemiesValue += stackValue;
            }
        }
        
        const target = enemies.find(e => e.unitId === goal?.targetId);
        const secondaryEnemies = enemies.filter(e => e.unitId !== goal?.targetId);

        const secondarySources = secondaryEnemies.map(e => ({ 
            id: e.unitId, 
            q: e.q, 
            r: e.r 
        }));

        const unitValue = EvaluatorUtils.calculateStackPower(unit);
        const targetValue = target ? EvaluatorUtils.calculateStackPower(target) : 0;

        const { reachableIds, reachableMap, parentMap } = PathCalculator.getReachableNodes(
            unit, 
            {currentStamina: unit.currentStamina, speed: unit.stats.speed},
            battleMap
        )
        
        const zocMap = ZocCalculator.calculate(enemies, battleMap);

        // 1. Визначаємо джерела для побудови тактичної карти
        let sources: { id: string; q: number; r: number }[] = [];

        if (goal?.type === GoalType.REPOSITION && goal.targetPosition) {
            // Режим МАРШУ: ціль — конкретна точка на карті
            const coords = idToCoords(goal.targetPosition);
            sources = [{ 
                id: 'march_target', 
                q: coords.q, 
                r: coords.r 
            }];
        } else {
            // Режим БОЮ: ціль — ворожі юніти
            const targetUnits = target ? [target] : enemies;
            sources = targetUnits.map(e => ({ 
                id: e.unitId, 
                q: e.q, 
                r: e.r 
            }));
        }

        // 2. Розраховуємо карту дистанцій та вартості переміщення
        const tacticalMap = PathsToEnemyCalculator.calculate(
            sources, 
            unit,
            zocMap,
            battleMap,
        );

        const secondaryTacticalMap = PathsToEnemyCalculator.calculate(
            secondarySources, 
            unit,
            zocMap,
            battleMap,
            40,
        );

        const zocMapReversed = ZocCalculator.calculate(allies, battleMap);
        const threatMap = ThreatMapCalculator.calculate(
            reachableMap, 
            enemies, 
            unit, 
            battleMap,
            zocMapReversed
        );

        const protectionMap = ProtectionMapCalculator.calculate(
            unit,
            allies,
            enemies, 
            battleMap
        );

        const unitAvgDmg = EvaluatorUtils.calculateAvgDmg(unit, false) * CombatCalculator.getEffectiveCount(unit.count);
        
        const unitTotalHp = getUnitTotalHp(unit);

        const storeState = useBattleStore.getState();
        
        // 4. Формуємо матрицю відстаней та ризики
        // const { distanceMatrix, oppRiskCache } = this.precomputeHexData(
        //     unit, 
        //     units, 
        //     enemies, 
        //     reachableIds, 
        //     battleMap
        // );

        return {
            round,
            unit,
            goal,
            allies,
            enemies,
            target,
            battleMap,
            reachableMap,
            parentMap,
            stance,
            weights,
            behavior,
            threatMap,
            zocMap,
            protectionMap,
            tacticalMap,
            secondaryTacticalMap,
            unitAvgDmg,
            unitTotalHp,
            unitValue,
            targetValue,
            totalEnemiesValue,
            totalAlliesValue,
            storeState
            // distanceMatrix,
            // oppRiskCache,
            // pathHexes,
            // pathsToEnemies,
        };
    }

    private static precomputeHexData(
        unit: BattleUnit,
        units: Map<string, BattleUnit>,
        enemies: BattleUnit[],
        reachableHexes: string[],
        battleMap: Map<string, Hex>,
    ): PrecomputedHexData {
        const distanceMatrix = new Map<string, HexDistanceInfo>();
        const oppRiskCache = new Map<string, number>();
        
        // Build distance matrix
        reachableHexes.forEach(hexId => {
            const hexDistanceInfo = DistanceMatrixCalculator.calculate(hexId, enemies);
            // const opportunityRisk = OpportunityRiskCalculator.calculate(unit.unitId, units, unit, hexId, battleMap);

            // oppRiskCache.set(hexId, opportunityRisk);
            distanceMatrix.set(hexId, hexDistanceInfo);
        });
        
        return { distanceMatrix, oppRiskCache };
    }

    
    
}